import { DraftConfiguration, Point, PrintArea, PrintType, Size } from '@/types';
import { clamp } from '@/utils/math/clamp';
import { getAreaOfSize } from '@/utils/math/getAreaOfSize';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import { getMaxScale } from '@/model/configuration/getMaxScale';
import {
  isDraftConfigurationWithText,
  isDraftDesignConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { isPixelDraftConfiguration } from '@/model/configuration/isPixelDraftConfiguration';
import { scaleSize } from '@/model/configuration/scaleSize';
import { getPrintAreaSize } from '@/model/printArea/getPrintAreaSize';
import { getIsDieCutStickerPrintType } from '@/model/printType/getIsDieCutStickerPrintType';
import { isEnlargeable } from '@/model/printType/isEnlargeable';

const getMinimumScale = (printType: PrintType, configuration: DraftConfiguration) => {
  if (isEnlargeable(printType)) {
    if (isDraftDesignConfiguration(configuration)) {
      const minimumScale = configuration.design.restrictions?.minimumScale;
      if (minimumScale) {
        return minimumScale / 100;
      }
    }

    if (isDraftConfigurationWithText(configuration)) {
      const style = configuration.text.style;
      return style.font.minimalSize / style.fontSize;
    }

    return 1;
  }

  return 0;
};

/**
 * @param {number} offsetFactor - design center offset relative to the old print area
 * @param {number} size - adjusted size of the configuration
 * @param {number} space - size of the next print area
 */
const getOffsetDirection = (offsetFactor: number, size: number, space: number) => {
  const offset = offsetFactor * space - size / 2;
  const diff = space - size;
  // if the size fits inside the space make sure that it is positioned inside
  return diff > 0 ? clamp(offset, diff) : offset;
};

export const getAdjustedOffset = (
  currentOffset: Point,
  currentSize: Size,
  currentPrintArea: PrintArea,
  nextSize: Size,
  nextPrintArea = currentPrintArea
) => {
  const currentPrintAreaSize = getPrintAreaSize(currentPrintArea);
  const nextPrintAreaSize = getPrintAreaSize(nextPrintArea);

  return {
    x: getOffsetDirection(
      (currentOffset.x + currentSize.width / 2) / currentPrintAreaSize.width,
      nextSize.width,
      nextPrintAreaSize.width
    ),
    y: getOffsetDirection(
      (currentOffset.y + currentSize.height / 2) / currentPrintAreaSize.height,
      nextSize.height,
      nextPrintAreaSize.height
    ),
  };
};

/**
 * calculates size and position of configuration for new print-area and -type
 * @param configuration a DraftConfiguration before any changes happen
 * @param printArea the new PrintArea the configuration switches to
 * @param printType the new PrintArea the configuration switches to
 */
export const getAdjustedScaleAndOffset = (
  configuration: DraftConfiguration,
  printArea: PrintArea,
  printType: PrintType
) => {
  const currentPrintArea = configuration.printArea;
  const currentPrintType = configuration.printType;
  const currentConfigurationSize = getCurrentSizeOfConfiguration(configuration);
  const isPixelConfiguration = isPixelDraftConfiguration(configuration);

  let configurationSizeOnNextPrintArea = configuration.size;
  if (isPixelConfiguration) {
    const dpi = printType.dpi;
    const currentDpi = currentPrintType.dpi;
    if (dpi !== currentDpi) {
      configurationSizeOnNextPrintArea = scaleSize(
        configurationSizeOnNextPrintArea,
        currentDpi / dpi
      );
    }
  }

  const maxScale = getMaxScale(
    configurationSizeOnNextPrintArea,
    configuration.rotation,
    printArea,
    printType,
    isPixelConfiguration,
    true
  );

  let scale: number;
  if (isDraftDesignConfiguration(configuration) && !configuration.design.restrictions.allowScale) {
    scale = 1;
  } else if (getIsDieCutStickerPrintType(printType)) {
    // scale sticker designs to the maximal possible size and move them to the top left
    return [maxScale, { x: 0, y: 0 }] as const;
  } else {
    // calculate the ratio of the area occupied by the configuration
    const occupiedArea = getAreaOfSize(currentConfigurationSize);
    const availableArea = Math.min(
      getAreaOfSize(getPrintAreaSize(currentPrintArea)),
      getAreaOfSize(currentPrintType.size)
    );
    const occupationRatio = occupiedArea / availableArea;

    const nextAvailableArea = Math.min(
      getAreaOfSize(getPrintAreaSize(printArea)),
      getAreaOfSize(printType.size)
    );
    const nextOccupiedArea = nextAvailableArea * occupationRatio;

    let scaleFactor = Math.sqrt(nextOccupiedArea / occupiedArea);
    if (isPixelConfiguration) {
      const dpi = printType.dpi;
      const currentDpi = currentPrintType.dpi;
      if (dpi !== currentDpi) {
        scaleFactor *= dpi / currentDpi;
      }
    }

    scale = clamp(
      configuration.scale * scaleFactor,
      maxScale,
      getMinimumScale(printType, configuration)
    );
  }

  const offset = getAdjustedOffset(
    configuration.offset,
    currentConfigurationSize,
    currentPrintArea,
    scaleSize(configurationSizeOnNextPrintArea, scale),
    printArea
  );

  return [scale, offset] as const;
};
